import { postRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';

/**
 * Reading a CAD drawing into the five stored layers.
 *
 * Two steps on purpose. A drawing's layers are named `01-buitenwanden`, `A-WALL`, `MEUBILAIR`
 * — nothing a machine can map without guessing, and a guess imports a floor's furniture as
 * its walls. So the first call describes what is in the file and the second applies the
 * mapping the administrator chose.
 *
 * Neither writes. The converted layers come back and go through the ordinary upload, which
 * stays the one place a floorplan is created.
 */

export type DrawingLayerTarget = 'Ignore' | 'Border' | 'Floor' | 'Room' | 'Workplaces' | 'Label';

export interface DrawingLayerSummary {
  readonly name: string;
  readonly paths: number;
  readonly closedPaths: number;
  readonly points: number;
  readonly texts: number;
  readonly total: number;
  readonly sampleText?: string;
}

export interface DrawingInspection {
  readonly layers: readonly DrawingLayerSummary[];
  readonly unitsPerMetre: number | null;
  readonly blockReferences: number;
  /** References whose block was not in the file — almost always an xref. */
  readonly unresolvedBlockReferences: number;
  readonly missingBlocks: readonly string[];
  readonly width: number;
  readonly height: number;
  /** The drawing's width in metres at its declared units, for a sanity check. */
  readonly widthMetres: number | null;
}

export interface DrawingConversion {
  readonly borderLayer: unknown;
  readonly floorLayer: unknown;
  readonly roomLayer: unknown;
  readonly workplacesLayer: unknown;
  readonly labelLayer: unknown;
  readonly unitsPerMetre: number | null;
  readonly counts: Record<string, number>;
  /** Coded, so the portal says them in the administrator's own language. */
  readonly warnings: readonly string[];
}

/** Why a drawing could not be read. Each needs different words and a different thing to go and do. */
export type DrawingImportError = 'binary-dxf' | 'unsupported-format' | 'unreadable-upload' | 'forbidden' | 'failed';

export interface DrawingImportResult<T> {
  readonly data?: T;
  readonly error?: DrawingImportError;
}

interface ErrorBody {
  readonly error?: string;
}

const failureOf = (status: number, body: unknown): DrawingImportError => {
  if (status === 401 || status === 403) {
    return 'forbidden';
  }

  const code = (body as ErrorBody | undefined)?.error;

  return code === 'binary-dxf' || code === 'unsupported-format' || code === 'unreadable-upload'
    ? code
    : 'failed';
};

/**
 * The file as base64, which is how it travels.
 *
 * Read as a data URL rather than as text: a DXF from a European CAD seat is not UTF-8, and
 * reading it as text mangles every accented layer name on the way in — after which the
 * mapping the admin chose matches nothing.
 */
export const readAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = (): void => {
    const result = typeof reader.result === 'string' ? reader.result : '';
    resolve(result.slice(result.indexOf(',') + 1));
  };

  reader.onerror = (): void => reject(new Error('unreadable'));
  reader.readAsDataURL(file);
});

export const inspectDrawing = async (
  officeId: string,
  file: File,
  keycloak: KeycloakContext,
): Promise<DrawingImportResult<DrawingInspection>> => {
  const content = await readAsBase64(file);
  const response = await postRequest<DrawingInspection>('floorplan/import/inspect', keycloak, {
    officeId,
    fileName: file.name,
    content,
  });

  if (response.error || !response.json) {
    return { error: failureOf(response.status, response.json) };
  }

  return { data: response.json };
};

export const convertDrawing = async (
  officeId: string,
  file: File,
  mapping: Record<string, DrawingLayerTarget>,
  keycloak: KeycloakContext,
  unitsPerMetre?: number | null,
): Promise<DrawingImportResult<DrawingConversion>> => {
  const content = await readAsBase64(file);
  const response = await postRequest<DrawingConversion>('floorplan/import/convert', keycloak, {
    officeId,
    fileName: file.name,
    content,
    mapping,
    // Omitted rather than sent as null, so the drawing's own declared units survive when the
    // administrator has not overridden them.
    unitsPerMetre: unitsPerMetre ?? undefined,
  });

  if (response.error || !response.json) {
    return { error: failureOf(response.status, response.json) };
  }

  return { data: response.json };
};
